import { ServiceResponse } from '@nx-smartmonkey/shared/hooks';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { getActiveProjectMutationKey, getFlatProjectsMutationKey } from '.';
import { projectService } from '../../application/ProjectService';
import { ProjectActionType } from '../../context/project/actions';
import { useProjectContext } from '../../context/project/context';
import { FlatProject } from '../../domain/project/FlatProject';
import { Project, UpdateProjectProps } from '../../domain/project/Project';
import useErrorMiddleware from '../errorMiddleware/useErrorMiddleware';
import { useShowSnackbarMessage } from '../snackbarMessage/useShowSnackbarMessage';
import { useRetrieveUserCredentials } from '../user/useRetrieveUserCredentials';

type UseUpdateProjectResponse = ServiceResponse<
  ({
    label,
    description,
    location,
    optimizer_config,
    traffic_options,
    units,
    time_zone,
    plans,
    tasks_enabled,
    geo_fences_enabled,
    constraints,
    customers,
    drivers,
    project_slug,
  }: UpdateProjectProps) => void,
  void
>;

// Hooks act as 'Adapter' layer.
export const useUpdateProject = ({ project }: { project: Project }): UseUpdateProjectResponse => {
  const { sessionExpiredLogout } = useErrorMiddleware();
  const { showSnackbarMessage } = useShowSnackbarMessage();

  const { data: userCredentials } = useRetrieveUserCredentials();

  const queryClient = useQueryClient();

  const { projectDispatch } = useProjectContext();

  const { isLoading, isSuccess, isError, mutate } = useMutation(
    (updateProps: UpdateProjectProps): Promise<Project> => {
      project.update(updateProps);
      return projectService.save({ project, subscriptionCredentials: userCredentials!.subscription });
    },
    {
      onMutate: async (updateProps: UpdateProjectProps) => {
        project.update(updateProps);

        // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
        await queryClient.cancelQueries(getFlatProjectsMutationKey());
        await queryClient.cancelQueries(getActiveProjectMutationKey());

        // Snapshot the project previous value
        const previousProjects = queryClient.getQueryData<Array<FlatProject>>(getFlatProjectsMutationKey());
        const previousActiveProject = queryClient.getQueryData<Project>(getActiveProjectMutationKey());

        projectDispatch({
          type: ProjectActionType.SET_ACTIVE_PROJECT,
          payload: { activeProject: project },
        });

        return { previousProjects, previousActiveProject };
      },
      // If the mutation fails, use the context we returned above
      onError: (err: any, variables: UpdateProjectProps, context: any) => {
        if (context?.previousProjects) {
          queryClient.setQueryData<Array<FlatProject>>(getFlatProjectsMutationKey(), context.previousProjects);
        }

        if (context?.previousActiveProject) {
          queryClient.setQueryData<Project>(getActiveProjectMutationKey(), context.previousActiveProject);
        }

        console.info(`Update Project Error: `, err?.response);

        if (err?.response?.status === 401) {
          sessionExpiredLogout();
          return;
        }
        showSnackbarMessage({
          messageIntlId: `infoerror.${err?.response?.data?.messageId ?? `network_error`}`,
        });
      },
      // Always refetch after error or success:
      onSettled: (data: Project | undefined, error: any, variables: any, context: any) => {
        if (!error) {
          projectDispatch({
            type: ProjectActionType.SET_ACTIVE_PROJECT,
            payload: { activeProject: project },
          });
        }
        queryClient.invalidateQueries({ queryKey: getFlatProjectsMutationKey() });
        queryClient.invalidateQueries({ queryKey: getActiveProjectMutationKey() });
      },
    }
  );

  return {
    run: (updateProps: UpdateProjectProps) => mutate(updateProps),
    error: isError,
    success: isSuccess,
    isLoading: isLoading,
  };
};
